import React, { useRef } from "react";

import { composeEventHandlers, KEYS } from "@excalidraw/common";

import { useTunnels } from "../../context/tunnels";
import { useUIAppState } from "../../context/ui-appState";
import { t } from "../../i18n";
import {
  useEditorInterface,
  useExcalidrawActionManager,
  useExcalidrawSetAppState,
} from "../App";
import { ExcalidrawLogo } from "../ExcalidrawLogo";
import { UserList } from "../UserList";
import { DotsIcon } from "../icons";
import DropdownMenu from "../dropdownMenu/DropdownMenu";
import DropdownMenuSub from "../dropdownMenu/DropdownMenuSub";
import { withInternalFallback } from "../hoc/withInternalFallback";

import * as DefaultItems from "./DefaultItems";

/**
 * Editable document name shown in the top-left bar. Bound to `appState.name`,
 * which is the same value used for save/export file naming (`App.getName()`),
 * so renaming here renames the document. This is the seam a dashboard backend
 * connects to later — it reads/writes `appState.name`, no UI change needed.
 */
const FileNameInput = () => {
  const appState = useUIAppState();
  const setAppState = useExcalidrawSetAppState();
  const editorInterface = useEditorInterface();

  // value captured on focus so Escape can revert to it
  const revertToRef = useRef<string>("");

  if (editorInterface.formFactor === "phone") {
    // no room for a rename field on phone — logo + icons only
    return null;
  }

  return (
    <input
      className="top-left-bar__filename"
      type="text"
      value={appState.name ?? ""}
      // plain "Untitled" hint; a dashboard supplies "Untitled 1/2/…" later.
      // (getName() still adds a datetime for unique export filenames.)
      placeholder={t("labels.untitled")}
      aria-label={t("labels.fileTitle")}
      data-testid="top-left-bar-filename"
      onFocus={(event) => {
        revertToRef.current = appState.name ?? "";
        event.currentTarget.select();
      }}
      onChange={(event) => setAppState({ name: event.currentTarget.value })}
      onKeyDown={(event) => {
        if (event.key === KEYS.ENTER) {
          event.currentTarget.blur();
        } else if (event.key === KEYS.ESCAPE) {
          setAppState({ name: revertToRef.current });
          event.currentTarget.blur();
        }
      }}
    />
  );
};

const MainMenu = Object.assign(
  withInternalFallback(
    "MainMenu",
    ({
      children,
      onSelect,
      logo,
      screen,
      trailing,
    }: {
      children?: React.ReactNode;
      /**
       * Called when any menu item is selected (clicked on).
       */
      onSelect?: (event: Event) => void;
      /**
       * Leading logo slot of the top-left bar. App-provided so clicking it can
       * navigate somewhere the library doesn't know about (the dashboard).
       * Defaults to a static, non-interactive logo. The menu itself opens from
       * the three-dots trigger after the file name, not from the logo.
       */
      logo?: React.ReactNode;
      /**
       * Rendered in the top-left bar between undo/redo and the trailing slot —
       * the screen (frame) control. App-provided because screen management uses
       * the editor API + scene mutations the library doesn't own.
       */
      screen?: React.ReactNode;
      /**
       * Rendered at the end of the top-left bar (after the screen control).
       * Used by the app to inject app-only controls — e.g. the Upgrade button,
       * which depends on subscription state the library must not know about.
       */
      trailing?: React.ReactNode;
    }) => {
      const { MainMenuTunnel } = useTunnels();
      const editorInterface = useEditorInterface();
      const appState = useUIAppState();
      const setAppState = useExcalidrawSetAppState();
      const actionManager = useExcalidrawActionManager();

      return (
        <MainMenuTunnel.In>
          <div className="top-left-bar">
            {logo ?? (
              <div className="top-left-bar__logo">
                <ExcalidrawLogo size="xs" />
              </div>
            )}

            <FileNameInput />

            <DropdownMenu open={appState.openMenu === "canvas"}>
              <DropdownMenu.Trigger
                onToggle={() => {
                  setAppState({
                    openMenu: appState.openMenu === "canvas" ? null : "canvas",
                    openPopup: null,
                    openDialog: null,
                  });
                }}
                data-testid="main-menu-trigger"
                className="main-menu-trigger"
              >
                {DotsIcon}
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                onClickOutside={() => setAppState({ openMenu: null })}
                onSelect={composeEventHandlers(onSelect, () => {
                  setAppState({ openMenu: null });
                })}
                className="main-menu"
                align="start"
              >
                {children}
                {editorInterface.formFactor === "phone" &&
                  appState.collaborators.size > 0 && (
                    <fieldset className="UserList-Wrapper">
                      <legend>{t("labels.collaborators")}</legend>
                      <UserList
                        mobile={true}
                        collaborators={appState.collaborators}
                        userToFollow={appState.userToFollow?.socketId || null}
                      />
                    </fieldset>
                  )}
              </DropdownMenu.Content>
            </DropdownMenu>

            {/* undo/redo are disabled in view mode (viewMode: false), so
                don't render dead buttons there */}
            {!appState.viewModeEnabled && (
              <div className="top-left-bar__group">
                {actionManager.renderAction("undo")}
                {actionManager.renderAction("redo")}
              </div>
            )}

            {screen}

            {trailing}
          </div>
        </MainMenuTunnel.In>
      );
    },
  ),
  {
    Trigger: DropdownMenu.Trigger,
    Item: DropdownMenu.Item,
    ItemLink: DropdownMenu.ItemLink,
    ItemCustom: DropdownMenu.ItemCustom,
    Group: DropdownMenu.Group,
    Separator: DropdownMenu.Separator,
    Sub: DropdownMenuSub,
    DefaultItems,
  },
);

export default MainMenu;
